import type { Settings } from '../ui/settings';

/**
 * The one and only owner of the AudioContext. Everything is created lazily on
 * the first user gesture (browser autoplay policy), and nothing in this module
 * runs at import time, so the sim test suite never touches Web Audio.
 *
 * Bus graph:  sources -> sfx/music gains -> master -> compressor -> speakers
 *             (+ a shared procedural-IR convolver as a send return into master)
 */
export class AudioEngine {
  ctx: AudioContext | null = null;
  master!: GainNode;
  music!: GainNode;
  sfx!: GainNode;
  reverbSend!: GainNode;
  activeVoices = 0;
  /** Lifetime sources started — QA observability, not mixing policy. */
  totalStarted = 0;
  /** Guitar plucks scheduled (outside the SFX voice cap by design). */
  musicNotes = 0;
  private settings: Settings = { musicVolume: 0.8, musicOn: true, sfxOn: true };
  private readonly onReady: (() => void)[] = [];

  /**
   * Opportunistic autoplay at boot: build the whole graph immediately and
   * try to run it. Browsers usually hand back a suspended context until the
   * first user gesture (autoplay policy) — in that case everything sits
   * armed and silent, and the gesture unlock's resume() makes it audible.
   * But returning players Chrome trusts (Media Engagement Index) or embeds
   * with autoplay delegation get music from the very first frame.
   */
  tryAutoplay(): void {
    this.ensureUnlocked();
  }

  /** Create/resume the context. Safe to call on every gesture. */
  ensureUnlocked(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -12;
    compressor.ratio.value = 6;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;
    compressor.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.connect(compressor);
    this.music = ctx.createGain();
    this.music.connect(this.master);
    this.sfx = ctx.createGain();
    this.sfx.connect(this.master);

    // Desert-canyon space: a procedurally generated exponentially decaying
    // noise impulse response with progressive damping.
    const convolver = ctx.createConvolver();
    const seconds = 2.0;
    const length = Math.floor(seconds * ctx.sampleRate);
    const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
    for (let channel = 0; channel < 2; channel += 1) {
      const data = impulse.getChannelData(channel);
      let lowpass = 0;
      for (let index = 0; index < length; index += 1) {
        const t = index / ctx.sampleRate;
        const coefficient = 0.2 + 0.75 * (t / seconds); // darkens over time
        lowpass += (Math.random() * 2 - 1 - lowpass) * (1 - coefficient);
        data[index] = lowpass * Math.exp(-t / 0.45);
      }
    }
    convolver.buffer = impulse;
    convolver.connect(this.master);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 1;
    this.reverbSend.connect(convolver);

    this.applySettings(this.settings);

    // Suspend outright when the tab hides: gain-only muting would let the
    // throttled music scheduler pile up stale notes for a burst on return.
    document.addEventListener('visibilitychange', () => {
      if (!this.ctx) return;
      if (document.hidden) this.ctx.suspend().catch(() => {});
      else this.ctx.resume().catch(() => {});
    });

    for (const callback of this.onReady) callback();
    this.onReady.length = 0;
  }

  /** Run once the context exists (immediately if it already does). */
  whenReady(callback: () => void): void {
    if (this.ctx) callback();
    else this.onReady.push(callback);
  }

  applySettings(settings: Settings): void {
    this.settings = settings;
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.music.gain.setTargetAtTime(settings.musicOn ? settings.musicVolume * 0.6 : 0, t, 0.05);
    this.sfx.gain.setTargetAtTime(settings.sfxOn ? 1 : 0, t, 0.05);
  }

  get now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  /** Track a source for the voice cap; auto-decrements when it ends. */
  countVoice(source: AudioScheduledSourceNode): void {
    this.activeVoices += 1;
    this.totalStarted += 1;
    source.addEventListener('ended', () => {
      this.activeVoices = Math.max(0, this.activeVoices - 1);
    });
  }
}

/** Module singleton (guards against HMR piling up AudioContexts). */
let engine: AudioEngine | null = null;

export function getEngine(): AudioEngine {
  if (!engine) engine = new AudioEngine();
  return engine;
}

/** Live-apply from the settings modal; harmless no-op before audio exists. */
export function applyAudioSettings(settings: Settings): void {
  engine?.applySettings(settings);
}
